import React from 'react';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, CheckCircle, Shield } from 'lucide-react';

const GaugeBar = ({ value, max, percent, label, unit, alert, thresholdLabel }) => {
  const colors = {
    vert: { bar: 'bg-emerald-500', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    orange: { bar: 'bg-amber-500', text: 'text-amber-400', bg: 'bg-amber-500/10' },
    rouge: { bar: 'bg-red-500', text: 'text-red-400', bg: 'bg-red-500/10' },
  };
  const c = colors[alert] || colors.vert;
  const pct = Math.min(percent || 0, 100);
  const displayValue = typeof value === 'number' ? value.toFixed(1) : (value || '0');

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-zinc-400 text-xs">{label}</span>
        <span className={`font-mono text-xs font-bold ${c.text}`}>
          {displayValue} / {max || 0} {unit}
        </span>
      </div>
      <div className="relative h-3 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${c.bar} rounded-full transition-all duration-500`}
          style={{ width: `${pct}%` }} />
        {/* Threshold marker */}
        <div className="absolute inset-y-0 right-0 w-px bg-zinc-500" style={{ left: '100%' }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-zinc-600 text-[10px]">{thresholdLabel || `Seuil : ${max} ${unit}`}</span>
        <span className={`text-[10px] font-mono ${c.text}`}>{pct.toFixed(0)}%</span>
      </div>
    </div>
  );
};

const RegulatoryDashboard = ({ analysis }) => {
  if (!analysis?.regulatory) return null;

  const reg = analysis.regulatory;
  const adr = reg.adr_1136 || {};
  const tmd = reg.tmd_342 || {};

  const regimeBadge = {
    LQ: { cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    '1.1.3.6': { cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
    '3.4.2': { cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Shield },
    'ADR_INTEGRAL': { cls: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  };

  const rb = regimeBadge[reg.regime] || regimeBadge['ADR_INTEGRAL'];
  const Icon = rb.icon;

  return (
    <div className="space-y-4" data-testid="regulatory-dashboard">
      {/* Regime Header */}
      <div className={`p-3 rounded-lg border ${reg.regime === 'ADR_INTEGRAL' ? 'bg-red-500/5 border-red-500/30' : reg.regime === '3.4.2' ? 'bg-amber-500/5 border-amber-500/30' : 'bg-emerald-500/5 border-emerald-500/30'}`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${rb.cls.split(' ')[1]}`} />
          <Badge variant="outline" className={rb.cls} data-testid="regime-badge">
            {reg.regime_label}
          </Badge>
        </div>
      </div>

      {/* Gauges */}
      <div className="space-y-4">
        {/* ADR 1.1.3.6 Points */}
        <GaugeBar
          value={adr.total_points || 0}
          max={adr.threshold || 1000}
          percent={adr.percent || 0}
          label="ADR 1.1.3.6 — Points"
          unit="pts"
          alert={adr.alert || 'vert'}
          thresholdLabel={`Exemption si ≤ ${adr.threshold || 1000} pts`}
        />

        {/* TMD 3.4.2 - High classes */}
        {(tmd.nec_high_classes || 0) > 0 && (
          <GaugeBar
            value={tmd.nec_high_classes || 0}
            max={tmd.threshold_high || 100}
            percent={tmd.percent_high || 0}
            label="TMD 3.4.2 — NEC classes hautes (1.1/1.2/1.3)"
            unit="kg"
            alert={tmd.alert_high || 'vert'}
            thresholdLabel={`Max ${tmd.threshold_high || 100} kg pour classes hautes`}
          />
        )}

        {/* TMD 3.4.2 - Global */}
        <GaugeBar
          value={tmd.nec_global || 0}
          max={tmd.threshold_global || 333}
          percent={tmd.percent_global || 0}
          label="TMD 3.4.2 — NEC globale"
          unit="kg"
          alert={tmd.alert_global || 'vert'}
          thresholdLabel={`Max ${tmd.threshold_global || 333} kg total`}
        />
      </div>

      {/* NEC Detail by division */}
      {reg.nec_by_division && Object.keys(reg.nec_by_division).length > 0 && (
        <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
          <h5 className="text-zinc-400 text-xs font-semibold mb-2">Détail NEC par division</h5>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            {Object.entries(reg.nec_by_division).map(([div, nec]) => (
              <div key={div} className="flex justify-between text-xs">
                <span className="text-zinc-500">Division {div}</span>
                <span className="font-mono text-white">{(nec || 0).toFixed(3)} kg</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs mt-2 pt-2 border-t border-zinc-800">
            <span className="text-zinc-300 font-semibold">Total NEC</span>
            <span className="font-mono text-white font-bold">{(reg.total_nec || 0).toFixed(3)} kg</span>
          </div>
        </div>
      )}

      {/* TMD Status */}
      <div className={`p-2 rounded-lg border text-xs ${tmd.conforme ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' : 'bg-red-500/5 border-red-500/20 text-red-400'}`}>
        <div className="flex items-center gap-2">
          {tmd.conforme ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          <span className="font-semibold">
            {tmd.conforme ? 'Transport conforme TMD simplifié' : 'Transport NON conforme TMD simplifié'}
          </span>
          <span className="text-zinc-500 ml-auto">
            {tmd.case === 'LQ' ? 'LQ' : tmd.case === 'cas1_14g_seul' ? 'Cas 1 : 1.4G seul' : 'Cas 2 : Mixte'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RegulatoryDashboard;
